import { useModalState } from "@clipboard-health/ui-components";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { type WorkplaceQuizQuestion } from "@src/appV2/WorkplaceQuiz/types";
import { generatePath, Route, Switch, useHistory, useLocation } from "react-router-dom";

import {
  SHIFT_DISCOVERY_SHIFT_BUNDLE_MODAL_FULL_PATH,
  SHIFT_DISCOVERY_SHIFT_CONFIRM_BOOKING_MODAL_FULL_PATH,
  SHIFT_DISCOVERY_SHIFT_DOUBLE_MODAL_FULL_PATH,
  SHIFT_DISCOVERY_SHIFT_MODAL_FULL_PATH,
} from "../ShiftDiscovery/paths";
import { QuizQuestionDialog } from "./Dialog/QuizQuestionDialog";
import { QuizRulesDialog } from "./Dialog/QuizRulesDialog";
import { WORKPLACE_QUIZ_QUESTION_PATH_FULL, WORKPLACE_QUIZ_RULES_PATH_FULL } from "./paths";

export interface WorkplaceQuizRouterProps {
  shiftId: string;
  workplaceId: string | undefined;
  facilityNotes: Array<{
    isVisibleToWorker: boolean;
    categoryTextVisibleToWorker: string;
    noteId: string | undefined;
    note: string | undefined;
    identifier: string | undefined;
  }>;
  isLoadingQuiz: boolean;
  quizQuestions: WorkplaceQuizQuestion[];
  isLoadingResult: boolean;
  isErrorResult: boolean;
  userPassedQuiz: boolean;
  stillMissingRequirements: boolean;
  submitQuiz: (chosenOptions: Record<string, string>) => Promise<void>;
}

export function WorkplaceQuizRouter(props: WorkplaceQuizRouterProps) {
  const {
    shiftId,
    workplaceId,
    facilityNotes,
    isLoadingQuiz,
    quizQuestions,
    isLoadingResult,
    isErrorResult,
    stillMissingRequirements,
    userPassedQuiz,
    submitQuiz,
  } = props;

  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isFromBundle = searchParams.get("fromBundle") === "true";
  const isFromDouble = searchParams.get("fromDouble") === "true";
  const modalStateRules = useModalState("open");
  const modalStateQuestions = useModalState("open");

  const logProperties = { shiftId, source: "booking", workplaceId };

  return (
    <Switch>
      <Route exact path={WORKPLACE_QUIZ_RULES_PATH_FULL}>
        <QuizRulesDialog
          modalState={modalStateRules}
          facilityNotes={facilityNotes}
          isLoading={isLoadingQuiz}
          onNavigateBack={() => {
            logEvent(APP_V2_USER_EVENTS.VIEWED_WORKPLACE_QUIZ_PAGE, logProperties);
            history.goBack();
          }}
          onStartQuiz={() => {
            const questionPath = generatePath(WORKPLACE_QUIZ_QUESTION_PATH_FULL, { shiftId });
            history.push(`${questionPath}${location.search}`);
            logEvent(APP_V2_USER_EVENTS.VIEWED_WORKPLACE_QUIZ_PAGE, logProperties);
          }}
        />
      </Route>
      <Route exact path={WORKPLACE_QUIZ_QUESTION_PATH_FULL}>
        <QuizQuestionDialog
          modalState={modalStateQuestions}
          quizQuestions={quizQuestions}
          isLoading={isLoadingResult}
          isError={isErrorResult}
          userPassedQuiz={userPassedQuiz}
          stillMissingRequirements={stillMissingRequirements}
          isBundle={isFromBundle || isFromDouble}
          onNavigateBack={() => {
            history.goBack();
          }}
          onQuizSubmit={async (chosenOptions: Record<string, string>) => {
            logEvent(APP_V2_USER_EVENTS.VIEWED_QUIZ_PASSED_DIALOG, logProperties);
            await submitQuiz(chosenOptions);
          }}
          onRetakeQuiz={() => {
            history.goBack();
          }}
          onBookShift={() => {
            if (isFromBundle) {
              history.push(generatePath(SHIFT_DISCOVERY_SHIFT_BUNDLE_MODAL_FULL_PATH, { shiftId }));
            } else if (isFromDouble) {
              history.push(generatePath(SHIFT_DISCOVERY_SHIFT_DOUBLE_MODAL_FULL_PATH, { shiftId }));
            } else {
              history.push(
                generatePath(SHIFT_DISCOVERY_SHIFT_CONFIRM_BOOKING_MODAL_FULL_PATH, { shiftId })
              );
            }
          }}
          onCloseAfterSubmit={() => {
            if (isFromBundle) {
              history.replace(
                generatePath(SHIFT_DISCOVERY_SHIFT_BUNDLE_MODAL_FULL_PATH, { shiftId })
              );
            } else if (isFromDouble) {
              history.replace(
                generatePath(SHIFT_DISCOVERY_SHIFT_DOUBLE_MODAL_FULL_PATH, { shiftId })
              );
            } else {
              history.replace(generatePath(SHIFT_DISCOVERY_SHIFT_MODAL_FULL_PATH, { shiftId }));
            }
          }}
        />
      </Route>
    </Switch>
  );
}
